import './styles.scss';

import { PlusOutlined } from '@ant-design/icons';
import { TextEditor } from '@components/modules/editor';
import { SelectContact } from '@components/modules/select-contact';
import {
    DATE_FORMAT,
    MAX_TRANSACTION_FINIX_AMOUNT,
    SERVICE_OTHER_OPTION_ID,
    WHITE_LABEL_SETTING_RESOURCE,
} from '@constants/index.constant';
import { ProductFrequencyEnum } from '@enums/frequency.enum';
import { QuoteStatus } from '@enums/quote-status.enum';
import { UserTypeEnum } from '@enums/user-type.enum';
import { IQuote } from '@interfaces/quote';
import { IService } from '@interfaces/service';
import { IUser } from '@interfaces/user';
import { WLSetting } from '@interfaces/white-label-setting';
import { useSelect } from '@refinedev/antd';
import {
    BaseKey,
    BaseRecord,
    useApiUrl,
    useCreate,
    useCustom,
    useGetIdentity,
    useNavigation,
    useOne,
    useUpdate,
} from '@refinedev/core';
import { formatPrice } from '@utils/resource';
import { Button, Col, DatePicker, Form, Input, InputNumber, notification, Row, Select } from 'antd';
import Response from 'data-access/responses/response';
import dayjs from 'dayjs';
import { cloneDeep, keyBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { PayoutWLContextProvider } from '../payouts/context';
import { WLSettingFields } from '../wl-setting/constants/white-label-setting-fields';

export interface IQuoteForm {
    contactId: string;
    date: Date;
    dueDate: Date;
    description: string;
    items: IQuoteItemForm[];
    paymentPlans: IQuote.IPlan[];
    termsAndConditions: string;
    status: QuoteStatus;
}

export interface IQuoteItemForm {
    frequency: ProductFrequencyEnum;
    id: string;
    margin: number;
    productId: string;
    productName: string;
    qty: number;
    serviceId: string;
    unitPrice: number;
}

export const QuoteCreateIndex: React.FC = () => {
    const { t } = useTranslation(['quote', 'common']);
    const [form] = Form.useForm();

    const { id } = useParams();

    const [serviceUpdated, setServiceUpdated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [status, setStatus] = useState<QuoteStatus>(QuoteStatus.Draft);

    const { mutate } = useCreate<BaseRecord>();
    const { mutate: updateMutate } = useUpdate<BaseRecord>();
    const { goBack, push } = useNavigation();
    const { data: userIdentity } = useGetIdentity<IUser.IUserIdentity>();
    const isTenant = userIdentity?.userType === UserTypeEnum.TENANT;
    const isPartner =
        userIdentity?.userType === UserTypeEnum.AFFILIATE_PARTNER ||
        userIdentity?.userType === UserTypeEnum.WHITELABEL;
    const apiUrl = useApiUrl();
    const wlSettingResource = `${apiUrl}/${WHITE_LABEL_SETTING_RESOURCE}`;
    const [currContactId, setCurrContactId] = useState<string>();
    const planItemsAction: {
        [planIdx: string]: {
            removeItem: (index: number | number[]) => void;
        };
    } = {};

    const { data: wlSetting } = useCustom<Response<WLSetting.IWLSetting>>({
        url: wlSettingResource,
        method: 'get',
        config: {
            headers: {
                Authorization: 'Bearer ' + localStorage.getItem('access_token'),
            },
        },
    });

    const maxMargin =
        wlSetting?.data?.data?.data?.[WLSettingFields.MAXIMUM_MARGIN_PERCENTAGE] || '0';

    const _charge = wlSetting?.data?.data?.data?.[WLSettingFields.SERVICE_CHARGE_PERCENTAGE] || '0';

    const { refetch: refetchQuote } = useOne<IQuote.Quote>({
        resource: 'v1/quotes',
        id: id as BaseKey,
        queryOptions: {
            enabled: false,
        },
    });

    useEffect(() => {
        if (id) {
            setQuoteDetail();
        }
    }, [id]);

    const setQuoteDetail = async () => {
        const quoteResult = await refetchQuote();

        if (quoteResult && quoteResult.data?.data && !form.getFieldValue('id')) {
            const quoteData = quoteResult?.data?.data;
            setCurrContactId(quoteData.contactId);

            if (
                quoteData?.status === QuoteStatus.Approved ||
                quoteData?.status === QuoteStatus.Rejected ||
                quoteData?.status === QuoteStatus.Paid
            ) {
                push('/404');
            }

            const items = quoteData.items.map((item) => {
                item.unitPrice = item.unitPrice ?? 0;
                return item;
            });

            form.setFieldsValue({
                id: quoteData.id,
                contactId: quoteData.contactId,
                description: quoteData.description,
                termsAndConditions: quoteData.termsAndConditions,
                items: items,
                date: dayjs(quoteData.date),
                dueDate: dayjs(quoteData.dueDate),
                paymentPlans: (quoteData.paymentPlans || []).map((plan) => {
                    plan.paymentDate = plan.paymentDueDate ? dayjs(plan.paymentDate) : undefined;
                    plan.paymentDueDate = plan.paymentDueDate
                        ? dayjs(plan.paymentDueDate)
                        : undefined;

                    (plan.paymentPlanItems || []).forEach((item) => {
                        const existedItem = (items || []).find((i) => i.id === item?.quoteItemId);
                        item.frequency = existedItem?.frequency;
                    });

                    return plan;
                }),
            });
        }
    };

    const { selectProps: serviceSelectProps, queryResult } = useSelect<IService>({
        resource: 'v1/services/active-services',
        optionLabel: 'name',
        optionValue: 'id',
        pagination: {
            mode: 'server',
            current: 1,
            pageSize: 20,
        },
        errorNotification: (error, _, __) => {
            if ((error as { statusCode: number })?.statusCode === 403) {
                return {
                    type: 'error',
                    message: t('notifications.errors.not_enough_permission_services', {
                        ns: 'common',
                    }),
                };
            } else {
                return {
                    type: 'error',
                    message: t('notifications.errors.get_services_failed', { ns: 'common' }),
                };
            }
        },
    });

    const services: any = keyBy(queryResult.data?.data, 'id');

    const onChangeService = (serviceId: string, index: number) => {
        const items = form.getFieldValue('items');
        items[index].productName = null;
        items[index].productId = undefined;

        items[index].unitPrice = null;
        items[index].qty = items[index].qty || 1;
        items[index].frequency = null;
        items[index].margin = 0;

        setServiceUpdated(!serviceUpdated);
    };

    const onChangeProduct = (productId: string, index: number) => {
        const items = form.getFieldValue('items');
        const products = services[items[index].serviceId].products;
        const selectedProduct = products.find((p: any) => p.id === productId);
        items[index].productName = selectedProduct.name;
        items[index].unitPrice = selectedProduct.price;
        items[index].qty = 1;
        items[index].frequency = selectedProduct.frequency;

        form.setFieldsValue({ items: items });
        setServiceUpdated(!serviceUpdated);
    };

    const getTotalPrice = () => {
        const items = form?.getFieldValue('items');
        if (items) {
            let total = 0;
            for (const item of items) {
                let itemTotal = item?.qty * item?.unitPrice || 0;
                if (item?.margin) {
                    itemTotal = (itemTotal * item.margin) / 100 + itemTotal;
                }
                total += itemTotal;
            }

            return total;
        }
        return 0;
    };

    const onChangeStatus = () => {
        setServiceUpdated(!serviceUpdated);
    };

    const onChangeItemFrequency = (_: ProductFrequencyEnum, formItem: IQuoteItemForm) => {
        _removePlanItem(formItem?.id);
        onChangeStatus();
    };

    const _handleRemovePlanItemForm = ({
        planIdx,
        planItemIndex,
    }: {
        planIdx: number;
        planItemIndex: number;
    }) => {
        if (planItemsAction && planItemsAction[planIdx]) {
            planItemsAction[planIdx].removeItem(planItemIndex);
        }
    };

    const _removePlanItem = (itemId: string) => {
        if (!itemId) return;
        const plans = ((form.getFieldValue('paymentPlans') || []) as IQuote.IPlan[]).filter(
            (plan) => plan?.id,
        );
        plans.forEach((plan, _planIdx) => {
            const _planItemIdx = (plan?.paymentPlanItems || [])
                .filter((pI) => pI?.id)
                .findIndex((planItem) => planItem?.quoteItemId === itemId);

            if (_planItemIdx !== -1) {
                _handleRemovePlanItemForm({
                    planIdx: _planIdx,
                    planItemIndex: _planItemIdx,
                });
            }
        });
    };

    const onRemovePlanItem = (formItem: IQuoteItemForm) => {
        if (!formItem) return;
        _removePlanItem(formItem?.id);
        onChangeStatus();
    };

    const _calcTotalQuoteItem = (item: IQuoteItemForm | undefined): number => {
        if (!item) return 0;
        const subTotal = (item?.qty ?? 0) * (item?.unitPrice ?? 0) || 0;
        const total = item?.margin ? (subTotal * item?.margin) / 100 + subTotal : subTotal;

        return total;
    };

    const onChangePlanItem = ({
        quoteItemId,
        planIdx,
        planItemIdx,
        planItemFieldName,
        quoteItemFieldName,
    }: {
        quoteItemId: string;
        planIdx: number;
        planItemIdx: number;
        planItemFieldName: keyof IQuote.IPaymentPlanItem;
        quoteItemFieldName: keyof IQuoteItemForm | null;
    }) => {
        if (!quoteItemId) return;

        const quoteItems = (form.getFieldValue('items') || []) as IQuoteItemForm[];

        const existedQuoteItem = quoteItems.find((q) => q?.id === quoteItemId);

        const plans = (form.getFieldValue('paymentPlans') as IQuote.IPlan[]) || [];

        if (!plans?.length) return;
        const changedPlan = plans[planIdx];

        if (!changedPlan?.paymentPlanItems?.length) return;

        let selectedPlanItem = changedPlan.paymentPlanItems[planItemIdx];
        if (quoteItemFieldName) {
            selectedPlanItem = {
                ...selectedPlanItem,
                [planItemFieldName]: existedQuoteItem
                    ? existedQuoteItem[quoteItemFieldName as keyof IQuoteItemForm]
                    : null,
            } as IQuote.IPaymentPlanItem;
        }

        //if frequency of  selected item is per-month, set percentage to 100% and calculate total
        const total = _calcTotalQuoteItem(existedQuoteItem);

        if (existedQuoteItem?.frequency === ProductFrequencyEnum.PER_MONTH) {
            selectedPlanItem.frequency = ProductFrequencyEnum.PER_MONTH;
            selectedPlanItem.percentage = 100;
            selectedPlanItem.total = total;
        } else {
            selectedPlanItem.frequency = ProductFrequencyEnum.ONE_TIME_PAYMENT;
            selectedPlanItem.percentage = 0;
            selectedPlanItem.total = 0;
        }

        changedPlan.paymentPlanItems[planItemIdx] = selectedPlanItem;

        form.setFieldsValue({
            paymentPlans: plans,
        });

        onChangeStatus();
    };

    const onChangePlanItemPercent = async (planIdx: number, index: number) => {
        const item: IQuote.IPaymentPlanItem = form.getFieldValue([
            'paymentPlans',
            planIdx,
            'paymentPlanItems',
            index,
        ]);

        calculatePlanItemPrice({ quoteItemId: item?.quoteItemId, currPlanItemId: item?.id });

        const items: IQuoteItemForm[] = form.getFieldValue('items') || [];
        const quoteItemIdx = items.findIndex((i) => i.id === item?.quoteItemId);
        form.validateFields([['items', quoteItemIdx, 'productId']]);
    };

    const calculatePlanItemPrice = ({
        quoteItemId,
        currPlanItemId,
    }: {
        quoteItemId: string;
        currPlanItemId?: string;
    }) => {
        const quoteItems = (form.getFieldValue('items') || []) as IQuoteItemForm[];
        const existedQuoteItem = quoteItems.find((q) => q?.id === quoteItemId);

        const plans = (form.getFieldValue('paymentPlans') as IQuote.IPlan[]) || [];
        if (!plans?.length) return;

        //handle re-calculate
        const changedPlanItems: IQuote.IPaymentPlanItem[] = [];
        plans.forEach((plan) => {
            const existedPlanItem = (plan?.paymentPlanItems || []).find(
                (_planItem) => _planItem?.quoteItemId === quoteItemId,
            );

            if (currPlanItemId && existedPlanItem?.id === currPlanItemId) {
                changedPlanItems.unshift(existedPlanItem);
                return;
            }

            if (existedPlanItem) {
                changedPlanItems.push(existedPlanItem);
            }
        });

        const total = _calcTotalQuoteItem(existedQuoteItem);
        let remainPercent = 100;
        let remainPrice = total;

        (changedPlanItems || []).forEach((changedItem) => {
            const percentage = changedItem?.percentage ?? 0;
            const itemPrice = Number(((total * percentage) / 100).toFixed(2));

            changedItem.frequency =
                existedQuoteItem?.frequency ?? ProductFrequencyEnum.ONE_TIME_PAYMENT;

            remainPercent = Math.max(remainPercent - percentage, 0);

            if (remainPercent > 0) {
                changedItem.total = itemPrice;
            } else {
                changedItem.total = remainPrice;
            }

            remainPrice = Math.max(remainPrice - changedItem.total, 0);
        });

        form.setFieldsValue({
            paymentPlans: plans,
        });
    };

    const onChangeMargin = ({ itemId, margin }: { margin: number | null; itemId: string }) => {
        if (margin && margin > 0) {
            calculatePlanItemPrice({ quoteItemId: itemId });
        }
        onChangeStatus();
    };

    const onChangeQuantity = ({ itemId, qty }: { qty: number | null; itemId: string }) => {
        if (qty && qty > 0) {
            calculatePlanItemPrice({ quoteItemId: itemId });
        }
        onChangeStatus();
    };

    const _handlePriceBeforeSubmit = (formVal: IQuoteForm): IQuoteForm | null => {
        if (!formVal) return null;
        const payload = cloneDeep(formVal) as IQuoteForm;
        for (const item of payload.items) {
            const basePrice = +Number.parseFloat(`${item?.unitPrice}`).toFixed(2);
            item.unitPrice = basePrice * 100;
        }
        return payload;
    };

    const onFinish = (values: IQuoteForm) => {
        const payload = _handlePriceBeforeSubmit(values);
        if (!payload) return;
        payload.status = status;
        const total = getTotalPrice();

        // total not > 50.000 cents show error
        if (total > MAX_TRANSACTION_FINIX_AMOUNT) {
            notification.error({
                message: t('quotes.error.total_price_exceed', {
                    limit: MAX_TRANSACTION_FINIX_AMOUNT,
                }),
                type: 'error',
            });
            return;
        }
        setIsLoading(true);

        if (id) {
            updateMutate(
                {
                    id: id,
                    resource: 'v1/quotes',
                    values: payload,
                    successNotification: { message: 'Successfully updated', type: 'success' },
                },
                {
                    onError: (_) => {
                        setIsLoading(false);
                    },
                    onSuccess: (_) => {
                        form.resetFields();
                        setTimeout(() => {
                            goBack();
                        });
                    },
                },
            );
        } else {
            mutate(
                {
                    resource: 'v1/quotes',
                    values: payload,
                    successNotification: { message: 'Successfully created', type: 'success' },
                    errorNotification: false,
                },
                {
                    onError: (error) => {
                        if (error.message === 'quote_item_lack_of_percentage') {
                            notification.error({
                                message: t('quotes.error.quote_item_lack_of_percentage'),
                                type: 'error',
                            });
                        } else {
                            notification.error({
                                message: error.message,
                                type: 'error',
                            });
                        }
                        setIsLoading(false);
                    },
                    onSuccess: (_) => {
                        setIsLoading(false);
                        goBack();
                    },
                },
            );
        }
        return;
    };

    const submitForm = async (status: QuoteStatus) => {
        setStatus(status);
        form.submit();
    };

    const disablePaymentDate = (current: dayjs.Dayjs): boolean => {
        return current < dayjs().startOf('day');
    };

    const disableDueDate = (current: dayjs.Dayjs): boolean => {
        const paymentDate = form.getFieldValue('date');
        return current < (paymentDate ? dayjs(paymentDate).startOf('day') : dayjs().startOf('day'));
    };

    const disablePaymentDueDate = (current: dayjs.Dayjs, index: number): boolean => {
        const paymentDate = form.getFieldValue('paymentPlans')[index].paymentDate;
        return current < (paymentDate ? dayjs(paymentDate).startOf('day') : dayjs().startOf('day'));
    };

    const validateQuoteItemsOnChangePlanItem = async () => {
        const items: IQuoteItemForm[] = form.getFieldValue('items') || [];
        for (let index = 0; index < items?.length; index++) {
            form.validateFields([['items', index, 'productId']]);
        }
    };

    const validateQuoteItemSumPercent = (value: string, quoteItemId: string) => {
        if (!value) return Promise.resolve();

        const plans: IQuote.IPlan[] = form.getFieldValue('paymentPlans') || [];

        if (!plans?.length) {
            return Promise.resolve();
        }

        let sumPercent = 0;
        let isPlanned = false; //check if quote item has been added to plan
        plans.forEach((plan) => {
            const existedItem = (plan?.paymentPlanItems || []).find(
                (i) => i?.quoteItemId === quoteItemId,
            );

            if (existedItem) {
                isPlanned = true;
                sumPercent += existedItem?.percentage ?? 0;
            }
        });

        if (!isPlanned) {
            return Promise.reject(t('quotes.fields.percentage.planned_required'));
        }

        if (sumPercent < 100 && isPlanned) {
            return Promise.reject(t('quotes.fields.percentage.full_percentage_required'));
        }
        return Promise.resolve();
    };

    const validateItemBeforeAddPlan = async (callback: () => void) => {
        const items = form.getFieldValue('items') || [];
        try {
            for (let idx = 0; idx < items?.length; idx++) {
                if (items[idx]?.productName) {
                    try {
                        await form.validateFields([
                            ['items', idx, 'productName'],
                            ['items', idx, 'unitPrice'],
                            ['items', idx, 'frequency'],
                        ]);
                    } catch (err) {
                        continue;
                    }
                }
            }
            callback();
        } catch (err) {
            return;
        }
    };

    const selectServiceOtherOptions = {
        label: t('quotes.fields.other_service.label'),
        value: SERVICE_OTHER_OPTION_ID,
    };

    const quoteFormItems = (
        <Form.List name="items">
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }, index: number) => {
                        const formItem = form.getFieldValue('items')[index] as IQuoteItemForm;

                        let total = formItem?.unitPrice * formItem?.qty || 0;
                        const type = services[formItem?.serviceId]?.type;

                        if (formItem?.margin) {
                            total += (total * formItem.margin) / 100;
                        }

                        const isSelectedOtherService =
                            formItem?.serviceId === selectServiceOtherOptions.value;

                        return (
                            <Row className="list-product-item item-custom" key={'items' + key}>
                                <Col xs={24} sm={6}>
                                    {isTenant && (
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'serviceId']}
                                            label={t('quotes.fields.item.label')}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t('quotes.fields.item.required'),
                                                },
                                            ]}
                                        >
                                            {!type || type == UserTypeEnum.TENANT ? (
                                                <Select
                                                    getPopupContainer={(trigger) =>
                                                        trigger.parentNode
                                                    }
                                                    options={
                                                        isTenant
                                                            ? [
                                                                  ...(serviceSelectProps.options?.filter(
                                                                      (item) =>
                                                                          item.value &&
                                                                          services[item.value]
                                                                              ?.type ===
                                                                              UserTypeEnum.TENANT,
                                                                  ) || []),
                                                                  selectServiceOtherOptions,
                                                              ]
                                                            : [
                                                                  ...(serviceSelectProps.options ||
                                                                      []),
                                                              ]
                                                    }
                                                    showSearch={false}
                                                    onChange={(item: any) => {
                                                        onChangeService(item, index);
                                                        onRemovePlanItem(formItem);
                                                    }}
                                                    placeholder={'Select service'}
                                                />
                                            ) : (
                                                <Select
                                                    options={[
                                                        {
                                                            label: services[formItem.serviceId]
                                                                ?.name,
                                                            value: services[formItem.serviceId]?.id,
                                                        },
                                                    ]}
                                                    disabled={true}
                                                ></Select>
                                            )}
                                        </Form.Item>
                                    )}
                                    {!isTenant && (
                                        <>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'serviceId']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t('quotes.fields.item.required'),
                                                    },
                                                ]}
                                                label={t('quotes.fields.item.label')}
                                            >
                                                <Select
                                                    getPopupContainer={(trigger) =>
                                                        trigger.parentNode
                                                    }
                                                    {...serviceSelectProps}
                                                    showSearch={false}
                                                    onChange={(item: any) => {
                                                        onChangeService(item, index);
                                                        onRemovePlanItem(formItem);
                                                    }}
                                                    placeholder={'Select service'}
                                                />
                                            </Form.Item>
                                        </>
                                    )}

                                    {formItem?.serviceId && isSelectedOtherService && (
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'productName']}
                                            label={t('quotes.fields.product.label')}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t('quotes.fields.product.required'),
                                                },
                                                {
                                                    validator: (_, value, ___) =>
                                                        validateQuoteItemSumPercent(
                                                            value,
                                                            formItem?.id,
                                                        ),
                                                },
                                            ]}
                                            className="product-custom"
                                        >
                                            <Input
                                                onChange={() => {
                                                    form.validateFields([
                                                        ['items', index, 'unitPrice'],
                                                        ['items', index, 'frequency'],
                                                    ]);
                                                    onRemovePlanItem(formItem);
                                                }}
                                            />
                                        </Form.Item>
                                    )}
                                    {formItem?.serviceId && !isSelectedOtherService && (
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'productId']}
                                            label={t('quotes.fields.product.label')}
                                            className="product-custom"
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t('quotes.fields.product.required'),
                                                },
                                                {
                                                    validator: (_, value, ___) =>
                                                        validateQuoteItemSumPercent(
                                                            value,
                                                            formItem?.id,
                                                        ),
                                                },
                                            ]}
                                        >
                                            <Select
                                                getPopupContainer={(trigger) => trigger.parentNode}
                                                showSearch={false}
                                                placeholder={t('quotes.fields.product.placeholder')}
                                                onChange={(item) => {
                                                    onChangeProduct(item, index);
                                                    onRemovePlanItem(formItem);
                                                }}
                                                disabled={
                                                    isTenant && type === UserTypeEnum.WHITELABEL
                                                }
                                            >
                                                {services[formItem?.serviceId]?.products.map(
                                                    (product: any) => {
                                                        return (
                                                            <Select.Option
                                                                key={product.id}
                                                                value={product.id}
                                                            >
                                                                {product.name}
                                                            </Select.Option>
                                                        );
                                                    },
                                                )}
                                            </Select>
                                        </Form.Item>
                                    )}
                                </Col>

                                <Col xs={24} sm={6}>
                                    {isTenant ? (
                                        <>
                                            <Form.Item
                                                {...restField}
                                                label={t('quotes.fields.unitPrice.label')}
                                                name={[name, 'unitPrice']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t(
                                                            'quotes.fields.unitPrice.required',
                                                        ),
                                                    },
                                                ]}
                                            >
                                                <InputNumber
                                                    precision={2}
                                                    formatter={(value) =>
                                                        `${value}`.replace(
                                                            /\B(?=(\d{3})+(?!\d))/g,
                                                            ',',
                                                        )
                                                    }
                                                    parser={(value) =>
                                                        value!.replace(/\$\s?|(,*)/g, '')
                                                    }
                                                    onChange={onChangeStatus}
                                                    disabled={type === UserTypeEnum.WHITELABEL}
                                                />
                                            </Form.Item>
                                            {type === UserTypeEnum.WHITELABEL &&
                                            Number.parseInt(_charge) > 0 ? (
                                                <span className="charge">
                                                    <b>{t('quotes.fields.service_fee.label')} :</b>
                                                    {`${Number.parseInt(_charge)}%`}
                                                </span>
                                            ) : null}
                                        </>
                                    ) : null}
                                    {isPartner ? (
                                        <>
                                            <span className="block mt-2">
                                                {formatPrice(formItem?.unitPrice * 1)}
                                            </span>
                                            {services[formItem?.serviceId]?.type ===
                                                UserTypeEnum.WHITELABEL &&
                                            Number.parseInt(_charge) > 0 ? (
                                                <span>
                                                    <b>{t('quotes.fields.service_fee.label')} :</b>
                                                    {`${Number.parseInt(_charge)}%`}
                                                </span>
                                            ) : null}
                                        </>
                                    ) : null}
                                    {/* enter margin when partner create quote */}
                                    {!isTenant &&
                                        formItem?.serviceId &&
                                        type === UserTypeEnum.TENANT && (
                                            <Form.Item
                                                {...restField}
                                                label={t('quotes.fields.margin.label')}
                                                name={[name, 'margin']}
                                                rules={[
                                                    {
                                                        validator: (_, value) => {
                                                            if (maxMargin && value > +maxMargin) {
                                                                return Promise.reject(
                                                                    t('common.max_margin_invalid', {
                                                                        ns: 'common',
                                                                        maxMargin: maxMargin,
                                                                    }),
                                                                );
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    },
                                                    {
                                                        required: true,
                                                        message: t('quotes.fields.margin.required'),
                                                    },
                                                ]}
                                            >
                                                <InputNumber
                                                    defaultValue={0}
                                                    min={0}
                                                    precision={0}
                                                    onChange={(margin: number | null) => {
                                                        onChangeMargin({
                                                            margin,
                                                            itemId: formItem?.id,
                                                        });
                                                    }}
                                                    placeholder={t(
                                                        'quotes.fields.margin.placeholder',
                                                    )}
                                                />
                                            </Form.Item>
                                        )}
                                </Col>
                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        {...restField}
                                        label={t('quotes.fields.qty.label')}
                                        name={[name, 'qty']}
                                        rules={[
                                            {
                                                required: true,
                                                message: t('quotes.fields.qty.required'),
                                            },
                                            {
                                                min: 1,
                                                type: 'number',
                                                message: 'Quantity cannot be less than 1',
                                            },
                                        ]}
                                    >
                                        <InputNumber
                                            min={1}
                                            defaultValue={1}
                                            step={1}
                                            precision={0}
                                            onChange={(qty: number | null) =>
                                                onChangeQuantity({ qty, itemId: formItem.id })
                                            }
                                            disabled={isTenant && type === UserTypeEnum.WHITELABEL}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={4}>
                                    <Form.Item
                                        {...restField}
                                        label={t('quotes.fields.frequency.label')}
                                        name={[name, 'frequency']}
                                        rules={[
                                            {
                                                required: true,
                                                message: t('quotes.fields.frequency.required'),
                                            },
                                        ]}
                                    >
                                        <Select
                                            getPopupContainer={(trigger) => trigger.parentNode}
                                            onChange={(e) => onChangeItemFrequency(e, formItem)}
                                            disabled={isTenant && type === UserTypeEnum.WHITELABEL}
                                        >
                                            <Select.Option
                                                value={ProductFrequencyEnum.ONE_TIME_PAYMENT}
                                            >
                                                {' '}
                                                {t(
                                                    'services.frequency.' +
                                                        ProductFrequencyEnum.ONE_TIME_PAYMENT,
                                                    { ns: 'common' },
                                                )}
                                            </Select.Option>
                                            <Select.Option value={ProductFrequencyEnum.PER_MONTH}>
                                                {' '}
                                                {t(
                                                    'services.frequency.' +
                                                        ProductFrequencyEnum.PER_MONTH,
                                                    { ns: 'common' },
                                                )}
                                            </Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={4} className="total-wrapper">
                                    <span className="total-price">
                                        {t('quotes.fields.total.label')}
                                    </span>
                                    <div className="total-container cursor-pointer">
                                        <span>{formatPrice(total)}</span>
                                        {(type === UserTypeEnum.WHITELABEL &&
                                            userIdentity?.userType === UserTypeEnum.TENANT) ||
                                            (index > 0 && (
                                                <img
                                                    className="ml-4"
                                                    src="/images/icons/remove.svg"
                                                    onClick={() => {
                                                        onRemovePlanItem(formItem);
                                                        remove(name);
                                                    }}
                                                />
                                            ))}
                                    </div>
                                </Col>
                            </Row>
                        );
                    })}

                    <div className="btn-add-container">
                        <Button
                            className="add-quote-item-btn"
                            onClick={() => {
                                add({
                                    qty: 1,
                                    id: uuidv4(),
                                });
                            }}
                            type="link"
                            icon={<PlusOutlined />}
                        >
                            {t('quotes.actions.addItem')}
                        </Button>
                    </div>
                </>
            )}
        </Form.List>
    );

    const sumPercentagePlanItem = (plans: IQuote.IPlan[], itemId: string): number => {
        if (!plans?.length) return 0;
        const sum = (plans || []).reduce((prev, current) => {
            const paymentPlanItem = (current?.paymentPlanItems || [])
                .filter((i) => i?.id)
                .find((i) => i.quoteItemId === itemId);
            prev += paymentPlanItem?.percentage ? paymentPlanItem.percentage : 0;
            return prev;
        }, 0);
        return sum;
    };

    const getAvailableQuoteItems = (currPlanIdx: number, currPlanItemIdx: number) => {
        const items = ((form.getFieldValue('items') as IQuoteItemForm[]) || []).filter(
            (item) =>
                (item?.productId || item?.productName) && item?.frequency && item?.unitPrice >= 0,
        );

        const quoteItems: IQuoteItemForm[] = [];
        const plans = ((form.getFieldValue('paymentPlans') || []) as IQuote.IPlan[]).filter(
            (plan) => plan?.id,
        );
        if (items?.length && plans?.length) {
            for (const item of items) {
                let valid = true;
                let isSelected = false;

                for (const plan of plans) {
                    const _planIdx = plans.findIndex((p) => p?.id === plan?.id);
                    const _paymentPlanItems = (plan.paymentPlanItems || []).filter((p) => p?.id);
                    if (_paymentPlanItems?.length) {
                        const existingIndex = _paymentPlanItems.findIndex(
                            (planItem: IQuote.IPaymentPlanItem) => planItem.quoteItemId === item.id,
                        );
                        const isExisting = existingIndex !== -1;

                        isSelected = currPlanItemIdx === existingIndex && _planIdx === currPlanIdx;
                        if (
                            (isExisting && item?.frequency === ProductFrequencyEnum.PER_MONTH) ||
                            (isExisting && _planIdx === currPlanIdx)
                        ) {
                            valid = false;
                            break;
                        }
                    }
                }

                if (valid || isSelected) {
                    quoteItems.push(item);
                }
            }
        }

        return quoteItems;
    };

    const paymentPlans = (
        <Form.List name="paymentPlans">
            {(listFields, { add, remove }) => (
                <>
                    <div className="section-header">
                        <div className="flex items-center">
                            <span className="payment-plans-title">
                                {t('quotes.fields.paymentPlan.label')}
                            </span>
                            <div className=" ml-auto">
                                <div className="btn-add-container">
                                    <Button
                                        onClick={() => {
                                            if (isTenant) {
                                                validateItemBeforeAddPlan(() =>
                                                    add({ id: uuidv4() }),
                                                );
                                            } else {
                                                add({ id: uuidv4() });
                                            }
                                        }}
                                        type="link"
                                        icon={<PlusOutlined />}
                                        className="add-payment-plans"
                                    >
                                        {t('quotes.actions.addPaymentPlan')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {listFields.length ? (
                        listFields.map(({ key: listKey, name, ...restField }, index: number) => {
                            return (
                                <div
                                    key={'paymentPlans' + listKey}
                                    className="plan-container py-6 px-4 mb-6"
                                >
                                    <div className="cursor-pointer flex justify-between">
                                        <span className="plan-index">{`#${
                                            index + 1 < 10 ? `0${index + 1}` : index + 1
                                        }`}</span>
                                        <img
                                            className="ml-auto"
                                            src="/images/icons/x-remove.svg"
                                            onClick={() => {
                                                remove(name);
                                                validateQuoteItemsOnChangePlanItem();
                                            }}
                                        />
                                    </div>
                                    <Row gutter={20}>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                className="date-label"
                                                {...restField}
                                                label={t('quotes.fields.paymentDate.label')}
                                                name={[name, 'paymentDate']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t(
                                                            'quotes.fields.paymentDate.required',
                                                        ),
                                                    },
                                                ]}
                                            >
                                                <DatePicker
                                                    getPopupContainer={(trigger) =>
                                                        trigger?.parentNode as HTMLElement
                                                    }
                                                    format={DATE_FORMAT}
                                                    disabledDate={(current) =>
                                                        disablePaymentDate(current)
                                                    }
                                                    onChange={() =>
                                                        form.resetFields([
                                                            [
                                                                'paymentPlans',
                                                                index,
                                                                'paymentDueDate',
                                                            ],
                                                        ])
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                {...restField}
                                                className="date-label"
                                                label={t('quotes.fields.paymentDueDate.label')}
                                                name={[name, 'paymentDueDate']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t(
                                                            'quotes.fields.paymentDueDate.required',
                                                        ),
                                                    },
                                                ]}
                                            >
                                                <DatePicker
                                                    getPopupContainer={(trigger) =>
                                                        trigger?.parentNode as HTMLElement
                                                    }
                                                    format={DATE_FORMAT}
                                                    disabledDate={(current) =>
                                                        disablePaymentDueDate(current, index)
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row className="table-header">
                                        <Col span={6} className="label-field-required">
                                            {t('quotes.fields.item.label')}
                                        </Col>
                                        <Col span={6} className="label-field-required">
                                            {t('quotes.fields.frequency.label')}
                                        </Col>
                                        <Col span={6} className="label-field-required">
                                            {t('quotes.fields.percentage.label')}
                                        </Col>
                                        <Col span={4}>{t('quotes.fields.total.label')}</Col>
                                        <Col span={2}></Col>
                                    </Row>
                                    {paymentPlanItems(name, index)}
                                </div>
                            );
                        })
                    ) : (
                        <div className="plan-term-conditions pb-4">
                            {/* <div className="flex items-center">
                                <div className="condition-index"></div>
                                <div className="text ml-4">
                                    {t('quotes.term_conditions.agree_to_pay')}
                                </div>
                            </div> */}
                            <div className="flex items-center">
                                <div className="condition-index"></div>
                                <div className="text ml-4">
                                    {t('quotes.term_conditions.agree_to_secure')}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </Form.List>
    );

    const paymentPlanItems = (name: number, planIdx: number) => {
        return (
            <Form.List name={[name, 'paymentPlanItems']}>
                {(subFields, { add: addItem, remove: removeItem }) => {
                    //extract remove item form
                    if (planItemsAction[`${planIdx}`]) {
                        planItemsAction[`${planIdx}`].removeItem = removeItem;
                    } else {
                        planItemsAction[`${planIdx}`] = {
                            removeItem,
                        };
                    }
                    return (
                        <>
                            {subFields.map(({ key: subKey, name: subName }, index: number) => {
                                const formPlanItem = form.getFieldValue([
                                    'paymentPlans',
                                    planIdx,
                                    'paymentPlanItems',
                                    index,
                                ]);
                                return (
                                    <Row
                                        gutter={20}
                                        className={`list-plan-item ${
                                            index + 1 === (subFields || []).length
                                                ? 'last-child'
                                                : ''
                                        }`}
                                        key={'paymentPlanItems' + subKey}
                                    >
                                        <Col xs={24} sm={6}>
                                            <Form.Item
                                                label={t('quotes.fields.item.label')}
                                                name={[subName, 'quoteItemId']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t(
                                                            'quotes.fields.quote_item.required',
                                                        ),
                                                    },
                                                ]}
                                            >
                                                <Select
                                                    getPopupContainer={(trigger) =>
                                                        trigger.parentNode
                                                    }
                                                    showSearch={false}
                                                    placeholder={t(
                                                        'quotes.fields.quote_item.placeholder',
                                                    )}
                                                    onChange={(id: string) =>
                                                        onChangePlanItem({
                                                            quoteItemId: id,
                                                            planIdx,
                                                            planItemIdx: index,
                                                            planItemFieldName: 'frequency',
                                                            quoteItemFieldName: 'frequency',
                                                        })
                                                    }
                                                >
                                                    {(
                                                        getAvailableQuoteItems(planIdx, index) || []
                                                    )?.map((quoteItem: IQuoteItemForm) => {
                                                        return quoteItem.productName ? (
                                                            <Select.Option
                                                                key={quoteItem.id}
                                                                value={quoteItem.id}
                                                            >
                                                                {quoteItem.productName}
                                                            </Select.Option>
                                                        ) : null;
                                                    })}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6}>
                                            <Form.Item
                                                name={[subName, 'frequency']}
                                                label={t('quotes.fields.frequency.label')}
                                                className="frequency-custom"
                                            >
                                                <span className="pl-3.5">
                                                    {formPlanItem?.frequency
                                                        ? t(
                                                              `services.frequency.${formPlanItem?.frequency}`,
                                                              { ns: 'common' },
                                                          )
                                                        : ''}
                                                </span>
                                                <Input className="hidden" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={6}>
                                            <Form.Item
                                                label={t('quotes.fields.percentage.label')}
                                                name={[subName, 'percentage']}
                                                rules={[
                                                    {
                                                        required: true,
                                                        message: t(
                                                            'quotes.fields.percentage.required',
                                                        ),
                                                    },
                                                    {
                                                        min: 1,
                                                        type: 'number',
                                                        message: t(
                                                            'quotes.fields.percentage.not_less_than_1',
                                                        ),
                                                    },
                                                    {
                                                        validator: (_, value: number, __) => {
                                                            if (!value) return Promise.resolve();
                                                            const plans = (form.getFieldValue(
                                                                'paymentPlans',
                                                            ) || []) as IQuote.IPlan[];
                                                            const currItem = form.getFieldValue([
                                                                'paymentPlans',
                                                                planIdx,
                                                                'paymentPlanItems',
                                                                index,
                                                            ]) as IQuote.IPaymentPlanItem;

                                                            if (!currItem?.quoteItemId)
                                                                return Promise.resolve();

                                                            const _sumPercentage =
                                                                sumPercentagePlanItem(
                                                                    plans,
                                                                    currItem?.quoteItemId,
                                                                );

                                                            const remainPercent =
                                                                100 - _sumPercentage + value;

                                                            if (remainPercent <= 0) {
                                                                return Promise.reject(
                                                                    t(
                                                                        'quotes.fields.percentage.full_percentage',
                                                                        { remainPercent },
                                                                    ),
                                                                );
                                                            }

                                                            if (value > remainPercent) {
                                                                return Promise.reject(
                                                                    t(
                                                                        'quotes.fields.percentage.invalid_remain_percentage',
                                                                        { remainPercent },
                                                                    ),
                                                                );
                                                            }

                                                            return Promise.resolve();
                                                        },
                                                    },
                                                ]}
                                            >
                                                <InputNumber
                                                    min={1}
                                                    max={100}
                                                    defaultValue={1}
                                                    step={1}
                                                    precision={0}
                                                    onChange={(val: number | null) => {
                                                        onChangePlanItemPercent(planIdx, index);
                                                    }}
                                                    disabled={
                                                        form.getFieldValue([
                                                            'paymentPlans',
                                                            planIdx,
                                                            'paymentPlanItems',
                                                            index,
                                                            'frequency',
                                                        ]) === ProductFrequencyEnum.PER_MONTH
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={4}>
                                            <Form.Item
                                                name={[subName, 'total']}
                                                label={t('quotes.fields.total.label')}
                                                className="total-custom"
                                            >
                                                <span className="pl-6">
                                                    {formatPrice(
                                                        form.getFieldValue([
                                                            'paymentPlans',
                                                            planIdx,
                                                            'paymentPlanItems',
                                                            index,
                                                            'total',
                                                        ]) ?? 0,
                                                    )}
                                                </span>
                                                <Input className="hidden" />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={2}>
                                            <div className="total-container button-delete-custom cursor-pointer">
                                                <img
                                                    className="ml-0"
                                                    src="/images/icons/red-remove.svg"
                                                    onClick={() => {
                                                        removeItem(subName);
                                                        validateQuoteItemsOnChangePlanItem();
                                                        onChangeStatus();
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                );
                            })}
                            <div className="btn-add-container">
                                <Button
                                    className="add-plan-item-btn"
                                    onClick={() =>
                                        addItem({
                                            id: uuidv4(),
                                            percentage: 0,
                                            frequency: null,
                                            total: 0,
                                        })
                                    }
                                    type="link"
                                    icon={<PlusOutlined />}
                                >
                                    {t('quotes.actions.addItem')}
                                </Button>
                            </div>
                        </>
                    );
                }}
            </Form.List>
        );
    };

    return (
        <PayoutWLContextProvider>
            <section className="block-container">
                <div className="block-heading">
                    <span>{id ? t('quotes.heading.editTitle') : t('quotes.heading.title')}</span>
                    <div className="header-actions edit-order">
                        <Button onClick={goBack}>{t('quotes.actions.cancel')}</Button>
                        <Button
                            type="dashed"
                            disabled={status === QuoteStatus.Pending && isLoading}
                            loading={status === QuoteStatus.Draft && isLoading}
                            onClick={() => submitForm(QuoteStatus.Draft)}
                        >
                            {t('quotes.actions.saveAsDraft')}
                        </Button>
                        <Button
                            type="primary"
                            disabled={status === QuoteStatus.Draft && isLoading}
                            loading={status === QuoteStatus.Pending && isLoading}
                            onClick={() => submitForm(QuoteStatus.Pending)}
                        >
                            {id ? t('quotes.actions.save') : t('quotes.actions.create')}
                        </Button>
                    </div>
                </div>
                <Form
                    layout="vertical"
                    form={form}
                    initialValues={{ items: [{ qty: 1, id: uuidv4() }] }}
                    onFinish={onFinish}
                >
                    <div className="section quote-information">
                        <div className="section-header ">
                            {t('quotes.heading.quoteInformation')}
                        </div>
                        <Row gutter={20}>
                            <Col xs={24} md={8} xl={8}>
                                <Form.Item
                                    label={t('quotes.fields.contact.label')}
                                    name="contactId"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('quotes.fields.contact.required'),
                                        },
                                    ]}
                                >
                                    <SelectContact
                                        contactId={currContactId ? currContactId : null}
                                        disabled={id ? true : false}
                                    />
                                </Form.Item>
                            </Col>
                            <Col md={0} xl={4}></Col>
                            <Col xs={24} md={8} xl={6}>
                                <Form.Item
                                    label={t('quotes.fields.expected_payment_date.label')}
                                    name="date"
                                    rules={[
                                        {
                                            required: true,
                                            message: t(
                                                'quotes.fields.expected_payment_date.required',
                                            ),
                                        },
                                    ]}
                                >
                                    <DatePicker
                                        getPopupContainer={(trigger) =>
                                            trigger?.parentNode as HTMLElement
                                        }
                                        format={DATE_FORMAT}
                                        disabledDate={(current) => disablePaymentDate(current)}
                                        onChange={() => form.resetFields(['dueDate'])}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={8} xl={6}>
                                <Form.Item
                                    label={t('quotes.fields.dueDate.label')}
                                    name="dueDate"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('quotes.fields.dueDate.required'),
                                        },
                                    ]}
                                >
                                    <DatePicker
                                        getPopupContainer={(trigger) =>
                                            trigger?.parentNode as HTMLElement
                                        }
                                        format={DATE_FORMAT}
                                        disabledDate={(current) => disableDueDate(current)}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                    <div className="section">
                        <Row gutter={20}>
                            <Col md={24}>
                                <Form.Item
                                    label={t('quotes.fields.description.label')}
                                    name="description"
                                >
                                    <TextEditor height="300px" />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                    <div className="section">
                        <div className="section-header">{t('quotes.heading.itemDetails')}</div>

                        <Row className="item-list">
                            <Col span={24}>
                                <Row
                                    className={` ${
                                        window.screen.width < 578 ? 'table-header-none' : ''
                                    } table-header`}
                                >
                                    <Col span={6} className="label-field-required">
                                        {t('quotes.fields.item.label')}
                                    </Col>
                                    <Col span={6} className="label-field-required">
                                        {t('quotes.fields.unitPrice.label')}
                                    </Col>
                                    <Col span={4} className="label-field-required">
                                        {t('quotes.fields.qty.label')}
                                    </Col>
                                    <Col span={4} className="label-field-required">
                                        {t('quotes.fields.frequency.label')}
                                    </Col>
                                    <Col span={4}>{t('quotes.fields.total.label')}</Col>
                                </Row>

                                {quoteFormItems}
                            </Col>
                        </Row>

                        <div className="sum-total flex">
                            <span>{t('quotes.heading.totalPrice')}</span>
                            <span className="ml-auto">{formatPrice(getTotalPrice())}</span>
                        </div>
                    </div>
                    <div className="section payment-plans">
                        <Row gutter={20}>
                            <Col md={24}>{paymentPlans}</Col>
                        </Row>
                    </div>
                    <div className="section">
                        <div className="section-header">{t('quotes.fields.tc.label')}</div>
                        <Row gutter={20}>
                            <Col md={24}>
                                <Form.Item name="termsAndConditions">
                                    <TextEditor />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>
                </Form>
            </section>
        </PayoutWLContextProvider>
    );
};
